import React, { useState, useContext, useRef, useEffect } from 'react';
import { UserContext } from '../../hooks/UserContext';
import useLogout from '../../hooks/useLogout';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import useAuth from '../../hooks/useAuth';
import { HostInfoContext } from '../../hooks/HostInfoContext';

const HeaderWrapper = styled.header`
  padding: 1rem 4rem;
  background-color: #0e4c60;

  @media (max-width: 1440px) {
    padding: 1rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderContainer = styled.div`
  max-width: 2200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const HeaderLogo = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;

  img {
    height: 3rem;
  }

  span {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.7;
  }
`;

const HeaderLink = styled.a`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #ffffff;
  text-decoration: none;
`;

const HeaderToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #ffffff;

  @media (max-width: 768px) {
    display: block;
  }
`;

const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.7;

  a {
    text-decoration: none;
    color: #ffffff;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    display: ${({ hamburger }) => (hamburger === 'open' ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: calc(100% + 1rem);
    left: 0;
    width: calc(100% + 2rem);
    background-color: #0e4c60;
    z-index: 2;
    padding-block: 1rem;
    left: -1rem;
    border-top: 1px solid #ffffff;
  }
`;

const Tooltip = styled.button`
  background: #d9d9d9;
  border: none;
  cursor: pointer;
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  top: -1px;
  position: relative;
`;

const TooltipButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;

  & img {
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    border: 4px solid #d9d9d9;
  }
`;

const VisuallyHidden = styled.button`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

export default function Header() {
  const hostInfo = useContext(HostInfoContext);
  const profilePicture = localStorage.getItem('profilePicture');
  const location = useLocation();
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const popoverContentRef = useRef(null);

  const toggleButton = () => setHamburgerOpen(!hamburgerOpen);
  const { user } = useContext(UserContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const { check_admin } = useAuth();
  const { logoutUser } = useLogout();

  useEffect(() => {
    if (isPopoverOpen && popoverContentRef.current) {
      popoverContentRef.current.focus();
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user) {
        const res = await check_admin();
        setIsAdmin(res);
      }
    };

    fetchAdminStatus();
  }, [user, check_admin]);

  const STATE_NAME = {
    'CA': 'California',
    'HI': "Hawai\u02BBi",
  };

  return (
    location.pathname !== '/login' &&
    location.pathname !== '/signup' &&
    location.pathname !== '/interests' && (
      <HeaderWrapper>
        <HeaderContainer>
          <HeaderLogo>
            <HeaderLink as={Link} to="/">
            <svg
              width="130"
              height="38"
              viewBox="0 0 130 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label={`AI Tip Sheets ${STATE_NAME[hostInfo?.STATE] || ''}`}
            >
              <path d="M2.83215e-06 15.7832L5.56932 0.592275H9.05014L14.7026 15.7832H11.2425L7.51234 5.1641C7.42922 4.92165 7.36341 4.69653 7.31492 4.48872C7.26643 4.27398 7.22141 4.06271 7.17985 3.8549H7.09672C7.06209 4.06271 7.01706 4.27398 6.96165 4.48872C6.90623 4.70345 6.84042 4.92858 6.76423 5.1641L3.05481 15.7832H2.83215e-06ZM3.16911 12.4998L3.86527 10.1308H10.5879L11.3049 12.4998H3.16911ZM15.9079 15.7832V0.654618H19.1809V15.7832H15.9079ZM25.6646 3.15873V0.654618H37.7591V3.15873H33.3535V15.7832H30.0701V3.15873H25.6646ZM39.2865 15.7832V5.0602H42.466V15.7832H39.2865ZM39.0268 1.96382C39.0268 1.49279 39.1861 1.09448 39.5047 0.768914C39.8303 0.443344 40.2875 0.28056 40.8763 0.28056C41.4651 0.28056 41.9188 0.443344 42.2374 0.768914C42.5561 1.08756 42.7154 1.48586 42.7154 1.96382C42.7154 2.44179 42.5561 2.84355 42.2374 3.16912C41.9188 3.49469 41.4616 3.65748 40.8659 3.65748C40.2771 3.65748 39.8234 3.49469 39.5047 3.16912C39.1861 2.84355 39.0268 2.44179 39.0268 1.96382ZM45.0429 20.1264V5.0602H48.0249L48.0873 6.28628H48.1392C48.3678 5.87065 48.7592 5.5243 49.3134 5.24722C49.8745 4.97014 50.4979 4.8316 51.1837 4.8316C52.5621 4.8316 53.6739 5.31996 54.519 6.29667C55.3641 7.27337 55.7867 8.58951 55.7867 10.2451V10.5776C55.7867 12.2193 55.3676 13.5319 54.5294 14.5156C53.6982 15.4992 52.6072 15.991 51.2564 15.991C50.5776 15.991 49.968 15.8767 49.4277 15.6481C48.8874 15.4195 48.4787 15.1044 48.2016 14.7026V20.1264H45.0429ZM48.2016 12.1777C48.3609 12.5725 48.6311 12.9154 49.012 13.2064C49.4 13.4973 49.8502 13.6428 50.3628 13.6428C51.104 13.6428 51.6547 13.3865 52.0149 12.8739C52.3751 12.3613 52.5552 11.6478 52.5552 10.7334V10.0892C52.5552 9.17484 52.3751 8.46482 52.0149 7.95915C51.6547 7.44655 51.104 7.19025 50.3628 7.19025C49.8156 7.19025 49.3619 7.33225 49.0017 7.61626C48.6415 7.90027 48.3748 8.23276 48.2016 8.61375V12.1777ZM62.3846 11.5335V11.1594H65.6473V11.4504C65.6473 12.1846 65.8758 12.7353 66.333 13.1025C66.7971 13.4696 67.4448 13.6531 68.2761 13.6531C69.0934 13.6531 69.7203 13.4834 70.1567 13.144C70.6001 12.8046 70.8217 12.3509 70.8217 11.7829C70.8217 11.2079 70.607 10.7611 70.1775 10.4425C69.748 10.1238 68.8268 9.77402 67.4136 9.39304C65.8343 8.97049 64.6394 8.39901 63.8289 7.6786C63.0185 6.9582 62.6132 5.98149 62.6132 4.74848C62.6132 3.41157 63.1154 2.34827 64.1199 1.55859C65.1243 0.761987 66.475 0.363684 68.1722 0.363684C69.8623 0.363684 71.22 0.768914 72.2452 1.57937C73.2704 2.38291 73.783 3.46352 73.783 4.82121V5.15371H70.5827V4.90434C70.5827 4.26705 70.3819 3.75792 69.9801 3.37693C69.5783 2.99595 68.9826 2.80545 68.1929 2.80545C67.4033 2.80545 66.8214 2.95785 66.4473 3.26264C66.0733 3.56742 65.8862 3.97958 65.8862 4.49911C65.8862 5.00478 66.094 5.39269 66.5097 5.66284C66.9322 5.933 67.8535 6.26203 69.2735 6.64994C70.8252 7.07942 72.0132 7.66129 72.8375 8.39555C73.6687 9.12981 74.0878 10.1758 74.0947 11.5335C74.0947 12.9674 73.5614 14.0826 72.4946 14.8792C71.4348 15.6758 70.0321 16.0741 68.2864 16.0741C66.5478 16.0741 65.1277 15.6862 64.0263 14.9104C62.9319 14.1346 62.3846 13.0089 62.3846 11.5335ZM75.9858 15.7832V1.58316e-05H79.1445V6.4733C79.4562 5.99534 79.8823 5.60397 80.4226 5.29918C80.9629 4.98746 81.6313 4.8316 82.4279 4.8316C83.4462 4.8316 84.2844 5.15025 84.9424 5.78753C85.6005 6.42482 85.9295 7.4604 85.9295 8.89429V15.7832H82.7604V9.14367C82.7604 8.45789 82.608 7.973 82.3032 7.68899C82.0054 7.39806 81.5967 7.25259 81.0772 7.25259C80.62 7.25259 80.2251 7.38421 79.8926 7.64743C79.5671 7.90373 79.3212 8.26394 79.1549 8.72805V15.7832H75.9858ZM87.8414 10.5776V10.2451C87.8414 8.58951 88.3471 7.25952 89.3584 6.2551C90.3697 5.25069 91.672 4.74848 93.2652 4.74848C94.8654 4.74848 96.1157 5.22298 97.0162 6.17198C97.9167 7.11405 98.367 8.41633 98.367 10.0788V11.1594H89.9091V9.25796H95.2498V9.05015C95.2498 8.47521 95.0766 7.99378 94.7303 7.60587C94.3909 7.21103 93.8991 7.01361 93.2548 7.01361C92.5137 7.01361 91.9491 7.28723 91.5612 7.83446C91.1802 8.3817 90.9897 9.11596 90.9897 10.0373V10.7126C90.9897 11.6547 91.1975 12.4028 91.6131 12.957C92.0288 13.5042 92.6383 13.7778 93.4419 13.7778C94.0099 13.7778 94.4948 13.6428 94.8965 13.3726C95.3052 13.0955 95.6343 12.7734 95.8836 12.4063L98.1903 13.7259C97.8509 14.4186 97.2725 14.9866 96.4551 15.4299C95.6447 15.8663 94.6576 16.0845 93.4938 16.0845C91.7274 16.0845 90.342 15.5858 89.3376 14.5883C88.3401 13.5839 87.8414 12.247 87.8414 10.5776ZM99.645 10.5776V10.2451C99.645 8.58951 100.151 7.25952 101.162 6.2551C102.173 5.25069 103.476 4.74848 105.069 4.74848C106.669 4.74848 107.919 5.22298 108.82 6.17198C109.72 7.11405 110.171 8.41633 110.171 10.0788V11.1594H101.713V9.25796H107.053V9.05015C107.053 8.47521 106.88 7.99378 106.534 7.60587C106.194 7.21103 105.703 7.01361 105.058 7.01361C104.317 7.01361 103.753 7.28723 103.365 7.83446C102.984 8.3817 102.793 9.11596 102.793 10.0373V10.7126C102.793 11.6547 103.001 12.4028 103.417 12.957C103.832 13.5042 104.442 13.7778 105.245 13.7778C105.814 13.7778 106.298 13.6428 106.7 13.3726C107.109 13.0955 107.438 12.7734 107.687 12.4063L109.994 13.7259C109.655 14.4186 109.076 14.9866 108.259 15.4299C107.448 15.8663 106.461 16.0845 105.297 16.0845C103.531 16.0845 102.146 15.5858 101.141 14.5883C100.144 13.5839 99.645 12.247 99.645 10.5776ZM110.773 7.3565V5.0602H117.569V7.3565H110.773ZM112.384 12.5621V6.10964L112.405 5.83948V2.37944H115.522V12.3647C115.522 12.8912 115.612 13.2514 115.792 13.4453C115.979 13.6324 116.242 13.7259 116.582 13.7259C116.748 13.7259 116.911 13.7086 117.07 13.6739C117.229 13.6324 117.385 13.5839 117.537 13.5285V15.7624C117.371 15.8455 117.129 15.9183 116.81 15.9806C116.498 16.043 116.149 16.0741 115.761 16.0741C114.729 16.0741 113.908 15.7936 113.298 15.2325C112.689 14.6645 112.384 13.7744 112.384 12.5621ZM118.639 12.3855L121.683 12.4063V12.5725C121.683 13.0782 121.85 13.4453 122.182 13.6739C122.521 13.8956 122.979 14.0064 123.554 14.0064C124.135 14.0064 124.572 13.9025 124.863 13.6947C125.161 13.48 125.31 13.189 125.31 12.8219C125.31 12.4617 125.147 12.195 124.821 12.0218C124.503 11.8417 123.775 11.6339 122.639 11.3984C121.358 11.1421 120.423 10.7438 119.834 10.2035C119.245 9.66319 118.951 8.95317 118.951 8.07344C118.951 7.04132 119.366 6.23086 120.197 5.64206C121.029 5.04634 122.158 4.74848 123.585 4.74848C125.005 4.74848 126.137 5.05673 126.982 5.67324C127.828 6.28281 128.25 7.12444 128.25 8.19813V8.33321H125.206V8.15657C125.206 7.74095 125.071 7.4223 124.8 7.20064C124.53 6.97205 124.118 6.85775 123.564 6.85775C123.024 6.85775 122.629 6.9478 122.379 7.12791C122.137 7.30801 122.016 7.55392 122.016 7.86563C122.016 8.17042 122.175 8.41633 122.494 8.60336C122.819 8.78346 123.561 8.98781 124.717 9.2164C126.006 9.4727 126.948 9.86407 127.544 10.3905C128.146 10.9101 128.448 11.6339 128.448 12.5621C128.448 13.6705 128.032 14.5363 127.201 15.1598C126.376 15.7763 125.171 16.0845 123.585 16.0845C122.012 16.0845 120.793 15.7763 119.927 15.1598C119.068 14.5363 118.639 13.6531 118.639 12.5102V12.3855Z" fill="white"/>
              <path d="M7.55892 17.7832L7.89449 19.801C8.51004 23.5023 11.2332 26.5017 14.8581 27.4707C11.2332 28.4398 8.51004 31.4392 7.89449 35.1405L7.55892 37.1583L7.22334 35.1405C6.6078 31.4392 3.8846 28.4398 0.259766 27.4707C3.8846 26.5017 6.6078 23.5023 7.22334 19.801L7.55892 17.7832Z" fill="white"/>
              <path d="M19.2165 17.7883L19.4024 18.9061C19.7434 20.9564 21.2519 22.6178 23.2598 23.1546C21.2519 23.6915 19.7434 25.3529 19.4024 27.4032L19.2165 28.521L19.0306 27.4032C18.6896 25.3529 17.1812 23.6915 15.1732 23.1546C17.1812 22.6178 18.6896 20.9564 19.0306 18.9061L19.2165 17.7883Z" fill="white"/>
              <text x="124" y="33" fontFamily="Roboto, Arial, sans-serif" fontSize="14" fill="white" textAnchor="end" letterSpacing="1px">{(STATE_NAME[hostInfo?.STATE] || '').toUpperCase()}</text>
            </svg>
          </HeaderLink>
            <HeaderToggle type="button" onClick={toggleButton}>
              ☰
            </HeaderToggle>
          </HeaderLogo>
          <HeaderNav hamburger={hamburgerOpen ? 'open' : undefined}>
            <Link to="/archive" state={{ reset: Date.now() }}>Archive</Link>
            <Link to="/about">About</Link>
            <Link to="/profile">Profile</Link>
            {isAdmin ? <Link to="/dashboard">Admin</Link> : null}
            {user ? (
              <Popover
                isOpen={isPopoverOpen}
                positions={['bottom']}
                onClickOutside={() => setPopoverOpen(false)}
                containerStyle={{ zIndex: 1 }}
                content={({ position, childRect, popoverRect }) => (
                  <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#D9D9D9'}
                    arrowSize={10}
                  >
                    <Tooltip
                      type="button"
                      onClick={logoutUser}
                      ref={popoverContentRef}
                    >
                      Sign Out
                      <VisuallyHidden
                        type="button"
                        onFocus={() => setPopoverOpen(false)}
                      >
                        Close Popover
                      </VisuallyHidden>
                    </Tooltip>
                  </ArrowContainer>
                )}
              >
                <TooltipButton
                  type="button"
                  onClick={() => setPopoverOpen(!isPopoverOpen)}
                >
                  {profilePicture && (
                    <img
                      src={profilePicture}
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      alt="Profile"
                    />
                  )}
                </TooltipButton>
              </Popover>
            ) : null}
          </HeaderNav>
        </HeaderContainer>
      </HeaderWrapper>
    )
  );
}
